import type { ExportAsyncForm } from "./export-async-form";
import type { ExportAsyncApi } from "./export-async-api";
import { translator } from "../translation/translator";
import { spinner } from "../util/spinner/spinner";

interface ExportStatusPayload {
    state?: string;
    name?: string;
    url?: string;
    size?: number;
    mtime?: number;
    message?: string;
}

const POLL_INTERVAL_MS = 3000;
const MAX_POLLS = 120; // ~6 minutes before we tell the admin to check back later
const ERROR_CLASS = "admin-download-card__status--error";

/**
 * Drives one export from the admin page (ISS-08-015): POST to generate, then —
 * for a big entity that was deferred to the scheduler — poll status until the
 * file exists, and render a download link into the card's status line.
 */
export class ExportAsyncHandler {
    private readonly form: ExportAsyncForm;

    private readonly api: ExportAsyncApi;

    private readonly inFlight = new Set<string>();

    public constructor(form: ExportAsyncForm, api: ExportAsyncApi) {
        this.form = form;
        this.api = api;
    }

    public async start(entity: string, statusBox: HTMLElement, button: HTMLButtonElement | null): Promise<void> {
        if (this.inFlight.has(entity)) {
            return;
        }
        this.inFlight.add(entity);
        // Spinner only inside the button (empty message) — the helper locks the
        // button width and disables it, so it never resizes while working. The
        // "generating…" caption goes in the status line beside the button.
        if (button) {
            spinner.showButtonLoader(button, { message: "" });
        }
        delete statusBox.dataset.autoDownloaded;
        this.renderMessage(statusBox, translator.translate("export_status_running"));

        try {
            const response = await this.api.generate(entity, this.form.collectParams());
            const payload = await this.readPayload(response);

            if (!response.ok || payload.state === "failed") {
                this.renderError(statusBox, payload.message);
                return;
            }

            if (payload.state === "done") {
                this.renderDone(statusBox, payload);
                return;
            }

            this.renderMessage(
                statusBox,
                translator.translate(payload.state === "pending" ? "export_status_pending" : "export_status_running"),
            );
            await this.poll(entity, statusBox);
        } catch {
            this.renderError(statusBox);
        } finally {
            this.inFlight.delete(entity);
            if (button) {
                spinner.hideButtonLoader(button);
            }
        }
    }

    private async poll(entity: string, statusBox: HTMLElement): Promise<void> {
        for (let attempt = 0; attempt < MAX_POLLS; attempt += 1) {
            await this.delay(POLL_INTERVAL_MS);

            let payload: ExportStatusPayload;
            try {
                payload = await this.readPayload(await this.api.status(entity));
            } catch {
                continue;
            }

            if (payload.state === "done") {
                this.renderDone(statusBox, payload);
                return;
            }
            if (payload.state === "failed") {
                this.renderError(statusBox, payload.message);
                return;
            }
        }

        // Still building after the poll budget — leave a message that reads
        // differently from the live "generating" one so it's clear the page
        // stopped watching, not that it's frozen.
        this.renderMessage(statusBox, translator.translate("export_status_slow"));
    }

    /** Unwrap the `{ success, message, data }` envelope down to the ExportStatus. */
    private async readPayload(response: Response): Promise<ExportStatusPayload> {
        const parsed = (await response.json().catch(() => null)) as unknown;
        if (parsed === null || typeof parsed !== "object") {
            return {};
        }
        const data = (parsed as { data?: unknown }).data;
        return data !== undefined && data !== null && typeof data === "object" ? (data as ExportStatusPayload) : {};
    }

    private renderMessage(box: HTMLElement, text: string): void {
        box.classList.remove(ERROR_CLASS);
        box.textContent = text;
        box.hidden = false;
    }

    private renderError(box: HTMLElement, detail?: string): void {
        box.classList.add(ERROR_CLASS);
        const base = translator.translate("export_status_failed");
        box.textContent = detail ? `${base} (${detail})` : base;
        box.hidden = false;
    }

    private renderDone(box: HTMLElement, payload: ExportStatusPayload): void {
        box.classList.remove(ERROR_CLASS);
        box.textContent = "";
        box.hidden = false;

        const label = document.createElement("span");
        label.textContent = `${translator.translate("export_status_ready")} `;
        box.appendChild(label);

        if (!payload.url) {
            return;
        }

        const link = document.createElement("a");
        link.href = payload.url;
        link.textContent = translator.translate("export_status_download");
        if (payload.name) {
            link.setAttribute("download", payload.name);
        }
        box.appendChild(link);

        if (typeof payload.size === "number" && payload.size > 0) {
            const meta = document.createElement("span");
            meta.textContent = ` (${(payload.size / 1048576).toFixed(1)} MB)`;
            box.appendChild(meta);
        }

        // Kick off the browser download once so the admin does not have to make a
        // second click for the common case.
        if (box.dataset.autoDownloaded !== "1") {
            box.dataset.autoDownloaded = "1";
            link.click();
        }
    }

    private delay(ms: number): Promise<void> {
        return new Promise((resolve) => {
            setTimeout(resolve, ms);
        });
    }
}
